import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Header } from "@/components/Header";
import { useScopedPath, useSlugScope } from "@/lib/scoped";
import { Archive, Crown, Trophy } from "lucide-react";
import { toast } from "sonner";

function formatPounds(pence) {
  if (!pence) return "£0.00";
  return `£${(pence / 100).toFixed(2)}`;
}

function shortDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function ChampionsPage() {
  const scope = useScopedPath();
  const slug = useSlugScope();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // iter96 — slug-scoped Hall of Fame call. Same rule as
        // SeasonsArchivePage: inside /c/[slug]/*, filter to that tenant.
        const url = slug
          ? `/champions?scope_slug=${encodeURIComponent(slug)}`
          : "/champions";
        const { data } = await api.get(url);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-700 inline-flex items-center gap-1.5">
            <Trophy className="h-3.5 w-3.5" /> Hall of Fame
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-950 mt-1">
            Past season champions.
          </h1>
          <p className="text-base text-zinc-500 mt-3 max-w-2xl">
            Every closed season is permanent. The Overall League resets when a season ends — but these names
            never come off the wall.
          </p>
          <div className="mt-5">
            <Link
              to={scope("/seasons-archive")}
              data-testid="hof-archive-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0038FF] hover:underline"
            >
              <Archive className="h-4 w-4" /> Browse the full past-seasons archive →
            </Link>
          </div>
        </div>

        {loading ? (
          <p className="text-sm uppercase tracking-[0.18em] text-zinc-500">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-16 text-center" data-testid="champions-empty">
            <p className="font-heading text-2xl font-semibold text-zinc-950">No champions yet.</p>
            <p className="mt-2 text-sm text-zinc-500">
              Close your first season and the winner will be immortalised here.
            </p>
            <Link to={scope("/leaderboard")} className="inline-block mt-6 text-sm font-semibold text-[#0038FF] underline">
              Visit the current Overall League →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="champions-grid">
            {rows.map((c) => {
              const champ = c.champion || {};
              const sid = c.season_id || c.id;
              return (
                <Link
                  key={c.id}
                  to={scope(`/seasons-archive/${sid}`)}
                  data-testid={`champion-card-${c.id}`}
                  className="relative overflow-hidden border border-amber-300 rounded-sm bg-gradient-to-br from-amber-50 via-white to-amber-50 p-6 block hover:shadow-md hover:border-amber-500 transition-all duration-150"
                >
                  <span className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-amber-300/30 blur-3xl" aria-hidden="true" />
                  <header className="flex items-center gap-4">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm bg-gradient-to-b from-amber-400 to-amber-600 text-white shadow shrink-0">
                      <Crown className="h-6 w-6" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-800 truncate">
                        {c.season_name || "Season"} · Closed {shortDate(c.closed_at)}
                      </p>
                      <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-zinc-950 mt-1 truncate" data-testid="champion-card-name">
                        {champ.name || "—"}
                      </h2>
                      {champ.total_points != null && (
                        <p className="text-xs text-zinc-500 mt-0.5 tabular-nums">
                          {champ.total_points} pts · {champ.matches_played} match-days
                        </p>
                      )}
                    </div>
                  </header>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
